import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Bell, Mail, MapPin, Tag, Loader2, Check, Send,
  Smartphone, Clock, Filter, ChevronRight, Volume2
} from "lucide-react";

// Category list for filtering
const CATEGORIES = [
  "Home Cleaning", "Plumbing", "Electrical", "HVAC", "Landscaping",
  "Painting", "Roofing", "Carpentry", "Moving", "Pest Control",
  "Appliance Repair", "Flooring", "Window Cleaning", "Pool Service",
  "Home Security", "Handyman", "General Contractor"
];

export default function NotificationSettingsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSend, setTestingSend] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");
  
  const [preferences, setPreferences] = useState({
    push_enabled: true,
    email_enabled: true,
    sms_enabled: true,
    email_digest_enabled: false,
    email_digest_frequency: "daily",
    category_filters: [],
    location_filter_enabled: false,
    location_radius_miles: 25,
    sound_enabled: true
  });

  // Check push notification support
  useEffect(() => {
    const checkPushSupport = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setPushSupported(true);
        const permission = Notification.permission;
        setPushPermission(permission);
        
        // Check if already subscribed
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushSubscribed(!!subscription);
      }
    };
    checkPushSupport();
  }, []);

  const fetchPreferences = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/pro/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Failed to load preferences:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const savePreferences = async (newPrefs) => {
    setSaving(true);
    try {
      await axios.put(`${API}/pro/notification-preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(prev => ({ ...prev, ...newPrefs }));
      toast.success("Preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key, value) => {
    const newPrefs = { [key]: value };
    savePreferences(newPrefs);
  };

  const handleCategoryToggle = (category) => {
    const current = preferences.category_filters || [];
    const newFilters = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    savePreferences({ category_filters: newFilters });
  };

  const enablePushNotifications = async () => {
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission !== 'granted') {
        toast.error("Push notifications were denied. Please enable them in your browser settings.");
        return;
      }

      // Get VAPID public key
      const keyResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = keyResponse.data.publicKey;

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      await axios.post(`${API}/pro/push-subscription`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPushSubscribed(true);
      toast.success("Push notifications enabled!");
    } catch (error) {
      console.error("Push subscription failed:", error);
      toast.error("Failed to enable push notifications");
    }
  };

  const disablePushNotifications = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API}/pro/push-subscription`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { endpoint: subscription.endpoint }
        });
      }
      
      setPushSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (error) {
      toast.error("Failed to disable push notifications");
    }
  };

  const sendTestDigest = async () => {
    setTestingSend(true);
    try {
      const response = await axios.post(`${API}/pro/send-test-digest`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestingSend(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notification-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Notification Settings</h1>
        <p className="text-slate-500">Customize how you receive lead alerts</p>
      </div>

      {/* Push Notifications */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#d4a853]" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Get instant alerts on your device when new leads arrive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pushSupported ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#1e3a5f]">Enable Push Notifications</p>
                  <p className="text-sm text-slate-500">
                    {pushSubscribed 
                      ? "You'll receive instant alerts when new leads match your preferences"
                      : "Allow browser notifications to never miss a lead"}
                  </p>
                </div>
                {pushSubscribed ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={disablePushNotifications}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="disable-push-btn"
                  >
                    Disable
                  </Button>
                ) : (
                  <Button 
                    size="sm"
                    onClick={enablePushNotifications}
                    className="bg-[#1e3a5f] hover:bg-[#152a45]"
                    data-testid="enable-push-btn"
                  >
                    Enable
                  </Button>
                )}
              </div>
              
              {pushPermission === "denied" && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Push notifications are blocked. Please enable them in your browser settings.
                </div>
              )}

              {preferences.push_enabled && (
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-slate-700">In-App Notifications</p>
                    <p className="text-xs text-slate-500">Show notification bell badge and dropdown</p>
                  </div>
                  <Switch
                    checked={preferences.push_enabled}
                    onCheckedChange={(v) => handleToggle("push_enabled", v)}
                    data-testid="in-app-toggle"
                  />
                </div>
              )}

              {/* Sound Settings */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Notification Sound</p>
                    <p className="text-xs text-slate-500">Play a sound when new leads arrive</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.sound_enabled !== false}
                  onCheckedChange={(v) => handleToggle("sound_enabled", v)}
                  data-testid="sound-toggle"
                />
              </div>
            </>
          ) : (
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              Push notifications are not supported in this browser.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Notifications */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#d4a853]" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Receive instant email alerts when new leads match your services
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-[#1e3a5f]">Enable Email Alerts</p>
              <p className="text-sm text-slate-500">Get an email for each new lead that matches your categories</p>
            </div>
            <Switch
              checked={preferences.email_enabled !== false}
              onCheckedChange={(v) => handleToggle("email_enabled", v)}
              data-testid="email-alerts-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* SMS Text Notifications */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#d4a853]" />
            SMS Text Notifications
          </CardTitle>
          <CardDescription>
            Receive text messages when new leads arrive (standard messaging rates may apply)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-[#1e3a5f]">Enable SMS Alerts</p>
              <p className="text-sm text-slate-500">Get a text message for each new lead that matches your categories</p>
            </div>
            <Switch
              checked={preferences.sms_enabled !== false}
              onCheckedChange={(v) => handleToggle("sms_enabled", v)}
              data-testid="sms-alerts-toggle"
            />
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            SMS will be sent to your registered phone number. Make sure your phone number is up to date in your profile.
          </div>
        </CardContent>
      </Card>

      {/* Email Digest */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#d4a853]" />
            Email Digest
          </CardTitle>
          <CardDescription>
            Receive a summary of new leads via email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-[#1e3a5f]">Enable Email Digest</p>
              <p className="text-sm text-slate-500">Get a summary of new leads delivered to your inbox</p>
            </div>
            <Switch
              checked={preferences.email_digest_enabled}
              onCheckedChange={(v) => handleToggle("email_digest_enabled", v)}
              data-testid="email-digest-toggle"
            />
          </div>

          {preferences.email_digest_enabled && (
            <>
              <div className="pt-3 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-700 mb-3">Digest Frequency</p>
                <div className="flex gap-3">
                  {["daily", "weekly"].map((freq) => (
                    <button
                      key={freq}
                      onClick={() => handleToggle("email_digest_frequency", freq)}
                      data-testid={`frequency-${freq}-btn`}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                        preferences.email_digest_frequency === freq
                          ? "border-[#d4a853] bg-[#d4a853]/5 text-[#1e3a5f]"
                          : "border-slate-200 hover:border-slate-300 text-slate-600"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium capitalize">{freq}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendTestDigest}
                  disabled={testingSend}
                  className="w-full"
                  data-testid="send-test-digest-btn"
                >
                  {testingSend ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Test Digest
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Category Filters */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#d4a853]" />
            Category Filters
          </CardTitle>
          <CardDescription>
            Only receive notifications for specific service categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3">
            <p className="text-sm text-slate-600">
              {preferences.category_filters?.length === 0 
                ? "You're receiving notifications for ALL categories"
                : `Filtering to ${preferences.category_filters?.length} categories`}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((category) => {
              const isSelected = preferences.category_filters?.includes(category);
              return (
                <button
                  key={category}
                  onClick={() => handleCategoryToggle(category)}
                  data-testid={`category-pill-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    isSelected
                      ? "bg-[#1e3a5f] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                  {category}
                </button>
              );
            })}
          </div>
          
          {preferences.category_filters?.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => savePreferences({ category_filters: [] })}
              className="mt-3 text-slate-500"
              data-testid="clear-filters-btn"
            >
              Clear all filters
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Location Filter */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#d4a853]" />
            Location Filter
          </CardTitle>
          <CardDescription>
            Only receive notifications for leads in your service area
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-[#1e3a5f]">Enable Location Filter</p>
              <p className="text-sm text-slate-500">Filter leads by distance from your location</p>
            </div>
            <Switch
              checked={preferences.location_filter_enabled}
              onCheckedChange={(v) => handleToggle("location_filter_enabled", v)}
              data-testid="location-filter-toggle"
            />
          </div>

          {preferences.location_filter_enabled && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-700 mb-3">Maximum Distance</p>
              <div className="flex gap-2">
                {[10, 25, 50, 100].map((miles) => (
                  <button
                    key={miles}
                    onClick={() => handleToggle("location_radius_miles", miles)}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      preferences.location_radius_miles === miles
                        ? "border-[#d4a853] bg-[#d4a853]/5 text-[#1e3a5f]"
                        : "border-slate-200 hover:border-slate-300 text-slate-600"
                    }`}
                  >
                    {miles} mi
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-[#1e3a5f] text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}
